import Button from '@pelckmans/elementis/components/button';
import Sidebar from '@pelckmans/elementis/components/sidebar';
import { useBreakpoint } from '@pelckmans/elementis/contexts/BreakpointsContext';
import SidebarIcon from '@pelckmans/elementis/icons/sidebar';
import { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet } from 'react-router-dom';
import useDocumentTitle from '../../../hooks/useDocumentTitle';
import ContentFooter from '../../../layout/content/ContentFooter';
import Footer from '../../../layout/footer/Footer';
import ApplicationNavigation from '../../../layout/navigation/page-level/ApplicationNavigation';
import PageNavigation from '../../../layout/navigation/page-level/PageNavigation';
import {
  withScrollThreshold,
  WithScrollThresholdProps,
} from '../../../shared-components/elementis/hoc/withScrollThreshold';
import { ExerciseCategoriesContextProvider } from '../_contexts/ExerciseCategoriesContext';
import SidebarVisibilityContext from '../_contexts/SidebarVisibilityContext';
import VirtualExerciseModuleHeader from './components/VirtualExerciseModuleHeader';
import VirtualExerciseModuleTOC from './components/VirtualExerciseModuleTOC';

interface VirtualExerciseModulePageProps extends WithScrollThresholdProps {}

function VirtualExerciseModulePage({ pastThreshold, pastThresholdForSidebar }: VirtualExerciseModulePageProps) {
  const { t } = useTranslation();

  useDocumentTitle(t('PAGES.VEM.DOCUMENT_TITLE'));

  const breakpoint = useBreakpoint();

  const { isSidebarVisible: sidebarVisible, setSidebarVisibility } = useContext(SidebarVisibilityContext);

  const overview = useMemo(() => ['tablet', 'mobile'].includes(breakpoint), [breakpoint]);
  const isSidebarVisible = useMemo(() => {
    if (breakpoint === 'desktop') return true;

    return sidebarVisible;
  }, [breakpoint, sidebarVisible]);

  return (
    <ExerciseCategoriesContextProvider includeScore includeCompletedExercises>
      <div className="page material-page material-page-with-sidebar">
        {breakpoint !== 'mobile' && <PageNavigation />}
        <div className="page-content">
          {breakpoint === 'mobile' && <ApplicationNavigation />}
          <div className="main-layout">
            <div className="main-layout-container">
              {isSidebarVisible && (
                <div className="sidebar-layout">
                  <Sidebar overview={overview}>
                    <Sidebar.Header
                      headerText={t('PAGES.VEM.SIDEBAR.HEADER_TEXT')}
                      density={overview && !pastThresholdForSidebar ? 'normal' : 'compact'}
                      contextButton={
                        <Button startIcon={<SidebarIcon />} onClick={() => setSidebarVisibility(false)}>
                          {t('PAGES.VEM.SIDEBAR.CONTEXT_BUTTON.CLOSE')}
                        </Button>
                      }
                    />
                    <Sidebar.Content>
                      <VirtualExerciseModuleTOC />
                    </Sidebar.Content>
                  </Sidebar>
                </div>
              )}
              <div className="content-layout">
                <main>
                  <div className="margin-wrapper">
                    <VirtualExerciseModuleHeader pastThreshold={pastThreshold} />
                    <Outlet />
                    <ContentFooter>
                      <Footer />
                    </ContentFooter>
                  </div>
                </main>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ExerciseCategoriesContextProvider>
  );
}

export default withScrollThreshold(VirtualExerciseModulePage, true);
